import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DialogTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Button } from "@/components/Button";
import { Checkbox } from "@/components/Checkbox";
import { Icon } from "@/components/Icon";
import { InfiniteScrollIndicator } from "@/components/InfiniteScrollIndicator";
import { Popover } from "@/components/Popover";
import { Skeleton } from "@/components/Skeleton";
import { Tooltip } from "@/components/Tooltip";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { SelectCell } from "./cells/SelectCell";
import type { CellContext, Column, DataTableProps, SortConfig, TableLayout, TableWidth } from "./DataTable.types";
import {
  AutoMobileRenderer,
  CardMobileRenderer,
  getMobileBreakpointClasses,
  MobileLoadingSkeleton,
} from "./MobileRenderer";
import { PaginationFooter, resolvePagination } from "./Pagination";
import { SortableHeaderRow } from "./SortableHeader";

/**
 * Column id of the checkbox column the selection model synthesizes.
 *
 * Double-underscored because it shares a namespace with consumer column ids and
 * must never collide with one; internal because a consumer does not declare it —
 * passing `onSelectionChange` is what asks for it.
 */
const SELECT_COLUMN_ID = "__select";

/** One shared empty set, so an uncontrolled render does not allocate per pass. */
const NO_SELECTION: ReadonlySet<string> = new Set<string>();

const densityClasses = {
  compact: "py-1.5 px-3 text-sm",
  default: "py-3 px-4 text-sm",
  relaxed: "py-4 px-4 text-base",
};

const alignmentClasses = {
  left: "text-left justify-start",
  center: "text-center justify-center",
  right: "text-right justify-end",
};

const widthClasses: Record<TableWidth, string> = {
  full: "w-full",
  auto: "w-auto inline-block",
  contained: "w-full max-w-6xl mx-auto",
};

const layoutClasses: Record<TableLayout, string> = {
  auto: "table-auto",
  fixed: "table-fixed",
  responsive: "table-auto",
};

/**
 * A column dimension in pixels, or 0 when it is not expressible in them.
 *
 * Only used to sum a table minimum width, so a percentage or `auto` contributes
 * nothing rather than guessing at a viewport.
 */
function toPx(value: string | number): number {
  if (typeof value === "number") return value;
  const match = /^(\d+(?:\.\d+)?)px$/.exec(value.trim());
  return match ? Number(match[1]) : 0;
}

function getColumnStyles<T>(column: Column<T>) {
  const styles: React.CSSProperties = {};

  if (column.width !== undefined) {
    styles.width = typeof column.width === "number" ? `${column.width}px` : column.width;
  }
  if (column.minWidth !== undefined) {
    styles.minWidth = typeof column.minWidth === "number" ? `${column.minWidth}px` : column.minWidth;
  }
  if (column.maxWidth !== undefined) {
    styles.maxWidth = typeof column.maxWidth === "number" ? `${column.maxWidth}px` : column.maxWidth;
  }
  if (column.flex !== undefined) {
    styles.flex = `${column.flex} 1 0`;
  }

  return styles;
}

function getCellValue<T>(row: T, column: Column<T>): any {
  if (column.accessor) {
    if (typeof column.accessor === "function") {
      return column.accessor(row);
    }
    return row[column.accessor];
  }
  return null;
}

function TableHeaderCell<T>({
  column,
  sortConfig,
  onSort,
  stickyHeader,
  stickyFirstColumn,
  isFirstColumn,
  density = "default",
  isLoading = false,
  headerContent,
  sortNote,
}: {
  column: Column<T>;
  sortConfig: SortConfig | null;
  onSort: (columnId: string) => void;
  stickyHeader?: boolean;
  stickyFirstColumn?: boolean;
  isFirstColumn?: boolean;
  density?: "compact" | "default" | "relaxed";
  isLoading?: boolean;
  /**
   * Replaces the label for this header. Used by the selection model for the
   * page-scoped header checkbox, which is a control rather than a label — the
   * alternative was a public `Column.renderHeader`, i.e. a new API for every
   * consumer to maintain in order to serve one internal column.
   */
  headerContent?: ReactNode;
  /** See `DataTableProps.sortNote`. Applies only when this column is the sorted one. */
  sortNote?: string;
}) {
  const isSorted = sortConfig?.columnId === column.id;
  const sortDirection = isSorted ? sortConfig.direction : null;
  const isSticky = stickyFirstColumn && isFirstColumn;
  // The note is about the sort, so it only exists where the sort is.
  const note = isSorted ? sortNote : undefined;

  return (
    <div
      className={twMerge(
        "flex items-center border-b border-border-default bg-background-surface",
        column.sortable ? "gap-2" : "gap-1",
        densityClasses[density],
        alignmentClasses[column.align || "left"],
        stickyHeader && "sticky top-0 z-10",
        isSticky && "sticky left-0 z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
        stickyHeader && isSticky && "z-30",
        column.sortable && !isLoading && "cursor-pointer hover:bg-background-hover",
        isLoading && "cursor-not-allowed opacity-60",
        isSorted ? "font-bold text-text-primary" : "font-medium text-text-secondary"
      )}
      onClick={() => !isLoading && column.sortable && onSort(column.id)}
      role="columnheader"
      // Untouched by `note`: the column really is sorted, and saying otherwise
      // here would hide the sort from assistive tech instead of explaining it.
      aria-sort={isSorted ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}
      aria-disabled={isLoading}
      title={note}
      data-sort-note={note}
      style={getColumnStyles(column)}
    >
      {isLoading ? (
        <Skeleton className="h-4 w-24" />
      ) : (
        <>
          {headerContent ?? <span className="truncate">{column.label}</span>}
          {column.headerTooltip && (
            // A focusable Button, not a bare Icon: the hint has to be reachable by
            // keyboard, and it needs an accessible name of its own since the icon is
            // decorative. Button also forwards TooltipTrigger's react-aria hover/focus
            // props — a plain DOM element drops them and the tooltip never opens.
            // No `onClick`: it stops the tooltip opening, and react-aria's press already
            // keeps the click from reaching the header's sort.
            <Tooltip content={column.headerTooltip}>
              <Button
                variant="ghost"
                aria-label={`About ${column.label}`}
                className="h-auto min-h-0 flex-shrink-0 p-0 text-text-tertiary hover:text-text-secondary"
              >
                <Icon name="Info" size={12} aria-hidden />
              </Button>
            </Tooltip>
          )}
          {note && <span className="sr-only">{note}</span>}
          {(column.sortable || isSorted) && (
            <div className="flex flex-shrink-0">
              {isSorted ? (
                <Icon
                  name={sortDirection === "asc" ? "CaretUp" : "CaretDown"}
                  size={14}
                  weight={note ? "regular" : "bold"}
                  className={note ? "text-text-secondary opacity-40" : "text-text-primary"}
                />
              ) : (
                <Icon name="CaretUpDown" size={12} className="text-text-secondary opacity-40" />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TableCell<T>({
  column,
  row,
  rowIndex,
  columnIndex,
  isHovered,
  isSelected,
  density = "default",
  className,
  stickyFirstColumn,
}: {
  column: Column<T>;
  row: T;
  rowIndex: number;
  columnIndex: number;
  isHovered: boolean;
  /** `undefined` when the table has no selection model — see `CellContext`. */
  isSelected?: boolean;
  density?: "compact" | "default" | "relaxed";
  className?: string;
  stickyFirstColumn?: boolean;
}) {
  const isSticky = stickyFirstColumn && columnIndex === 0;
  const value = getCellValue(row, column);

  // Create the cell context
  const context: CellContext = {
    isLoading: false,
    isHovered,
    // The field `DataTable.types.ts` has declared all along, finally assigned.
    // Spread conditionally so a table with no selection model leaves it
    // `undefined` rather than telling every cell it is "not selected".
    ...(isSelected === undefined ? {} : { isSelected }),
    rowIndex,
    columnIndex,
    density,
  };

  let content;

  if (column.cell) {
    // Use the cell component
    const CellComponent = column.cell;
    const cellProps = typeof column.cellProps === "function" ? column.cellProps(value, row) : column.cellProps;

    content = <CellComponent value={value} row={row} context={context} {...cellProps} />;
  } else if (column.render) {
    // Use custom render function
    content = column.render(value, row, context);
  } else {
    // Default rendering
    content = <span className="truncate">{value?.toString() ?? ""}</span>;
  }

  // Wrap with popover if configured
  if (column.popover) {
    const popoverConfig = column.popover;

    // Parse popover configuration
    let popoverContent: React.ReactNode;
    let placement: "top" | "bottom" | "left" | "right" = "top";
    let showArrow = false;
    let trigger: "hover" | "click" = "hover";

    if (typeof popoverConfig === "string") {
      // Simple string tooltip
      popoverContent = popoverConfig;
    } else if (typeof popoverConfig === "function") {
      // Function that returns content
      popoverContent = popoverConfig(value, row);
    } else {
      // Object configuration
      popoverContent =
        typeof popoverConfig.content === "function" ? popoverConfig.content(value, row) : popoverConfig.content;
      placement = popoverConfig.placement || "top";
      showArrow = popoverConfig.showArrow ?? false;
      trigger = popoverConfig.trigger || "hover";
    }

    const triggerProps = trigger === "hover" ? { trigger: "longPress" as const } : {};

    content = (
      <DialogTrigger {...triggerProps}>
        <div className="cursor-help">{content}</div>
        <Popover placement={placement} showArrow={showArrow}>
          <div className="p-2">{popoverContent}</div>
        </Popover>
      </DialogTrigger>
    );
  }

  return (
    <div
      className={twMerge(
        "flex items-center border-b border-border-default bg-background-surface",
        column.noCellPadding ? "" : densityClasses[density],
        alignmentClasses[column.align || "left"],
        isSticky && "sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
        className
      )}
      role="cell"
      style={getColumnStyles(column)}
    >
      {content}
    </div>
  );
}

function LoadingCell({
  column,
  rowIndex,
  columnIndex,
  density = "default",
  stickyFirstColumn,
}: {
  column: Column<any>;
  rowIndex: number;
  columnIndex: number;
  density?: "compact" | "default" | "relaxed";
  stickyFirstColumn?: boolean;
}) {
  const isSticky = stickyFirstColumn && columnIndex === 0;
  const context: CellContext = {
    isLoading: true,
    rowIndex,
    columnIndex,
    density,
  };

  let content;

  if (column.cell) {
    // Use the cell component's loading state
    const CellComponent = column.cell;
    const cellProps = typeof column.cellProps === "function" ? column.cellProps(null, {} as any) : column.cellProps;

    content = <CellComponent value={null} row={{} as any} context={context} {...cellProps} />;
  } else if (column.render) {
    // Use custom render function with loading context
    content = column.render(null, {} as any, context);
  } else {
    // Default loading skeleton
    content = <Skeleton className="h-4 w-full max-w-[200px]" />;
  }

  return (
    <div
      className={twMerge(
        "flex items-center border-b border-border-default bg-background-surface",
        column.noCellPadding ? "" : densityClasses[density],
        alignmentClasses[column.align || "left"],
        isSticky && "sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
      )}
      role="cell"
      style={getColumnStyles(column)}
    >
      {content}
    </div>
  );
}

/**
 * Check if an element or any of its ancestors up to the boundary is an interactive element.
 * Interactive elements include buttons, links, inputs, selects, textareas, and elements
 * with interactive ARIA roles.
 */
function isInteractiveElement(target: HTMLElement | null, boundary: HTMLElement): boolean {
  const interactiveTags = new Set(["BUTTON", "A", "INPUT", "SELECT", "TEXTAREA"]);
  const interactiveRoles = new Set(["button", "link", "checkbox", "switch", "menuitem", "option"]);

  let el = target;
  while (el && el !== boundary) {
    if (interactiveTags.has(el.tagName)) {
      return true;
    }

    const role = el.getAttribute("role");
    if (role && interactiveRoles.has(role)) {
      return true;
    }

    el = el.parentElement;
  }

  return false;
}

function TableRow<T>({
  columns,
  row,
  rowIndex,
  density = "default",
  onClick,
  stickyFirstColumn,
  isSelected,
}: {
  columns: Column<T>[];
  row: T;
  rowIndex: number;
  density?: "compact" | "default" | "relaxed";
  onClick?: (row: T) => void;
  stickyFirstColumn?: boolean;
  /** `undefined` when the table has no selection model. */
  isSelected?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onClick) return;

      // Skip row click if the click originated from an interactive element
      // (e.g., button, link, input) to prevent both cell and row handlers from firing
      const target = event.target as HTMLElement | null;
      const currentTarget = event.currentTarget;

      if (isInteractiveElement(target, currentTarget)) {
        return;
      }

      onClick(row);
    },
    [onClick, row]
  );

  return (
    <div
      className={twMerge("contents group", onClick && "cursor-pointer")}
      role="row"
      // Only when the table has a selection model: a row that can't be selected
      // must not announce that it isn't.
      aria-selected={isSelected === undefined ? undefined : isSelected}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {columns.map((column, columnIndex) => (
        <TableCell
          key={column.id}
          column={column}
          row={row}
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          isHovered={isHovered}
          isSelected={isSelected}
          density={density}
          // `bg-background-selected` last so `twMerge` lets it win over the
          // cell's own `bg-background-surface`; the hover class stays a
          // `group-hover:` variant, so the two do not collide.
          className={twMerge(onClick ? "group-hover:bg-background-hover" : "", isSelected && "bg-background-selected")}
          stickyFirstColumn={stickyFirstColumn}
        />
      ))}
    </div>
  );
}

function LoadingRow({
  columns,
  rowIndex,
  density = "default",
  stickyFirstColumn,
}: {
  columns: Column<any>[];
  rowIndex: number;
  density?: "compact" | "default" | "relaxed";
  stickyFirstColumn?: boolean;
}) {
  return (
    <div className="contents" role="row">
      {columns.map((column, columnIndex) => (
        <LoadingCell
          key={column.id}
          column={column}
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          density={density}
          stickyFirstColumn={stickyFirstColumn}
        />
      ))}
    </div>
  );
}

/**
 * DataTable
 *
 * Advanced table component with sorting, filtering, pagination, and infinite scroll.
 * Supports custom cell renderers, column configurations, multiple display densities,
 * and virtualization for large datasets.
 */
export function DataTable<T extends Record<string, unknown>>({
  columns,
  /*
   * Renamed on the way in: `data` below is the PAGE, sliced by the pagination
   * block at the top of this component. Every existing read of `data` — the
   * virtualizer, the selection page, the mobile list, the empty state — then sees
   * the rows on screen without knowing paging exists. Without pagination the two
   * are the same array.
   */
  data: allData,
  className,
  density = "default",
  width = "full",
  height,
  maxHeight,
  layout = "auto",
  mobileRenderer = "auto",
  customMobileRowRender,
  mobileBreakpoint = "md",
  isLoading = false,
  loadingState,
  loadingRowCount = 5,
  onLoadMore,
  hasMore = false,
  enableVirtualization,
  estimatedRowHeight,
  loadingIndicator,
  stickyHeader = false,
  stickyFirstColumn = false,
  onRowClick,
  getRowId,
  selectedRowIds,
  onSelectionChange,
  selectionMode = "multi",
  hideHeader = false,
  sortConfig: controlledSortConfig,
  onSort,
  sortNote,
  "aria-label": ariaLabel,
  enableColumnReorder = false,
  columnOrder: controlledColumnOrder,
  onColumnOrderChange,
  pagination,
}: DataTableProps<T>) {
  // ── Pagination ────────────────────────────────────────────────────────────
  //
  // Resolved FIRST, and the page slice is bound to `data` for the rest of the
  // component. Everything downstream — selection's page-scoped header checkbox,
  // the virtualizer, the mobile list, the empty state — then reads the rows that
  // are actually on screen, with no per-site awareness of paging at all.
  const paginationEnabled = pagination !== undefined && pagination !== false;
  const paginationConfig = typeof pagination === "object" ? pagination : undefined;
  const controlledPage = paginationConfig?.page;
  const isPageControlled = controlledPage !== undefined;
  const [uncontrolledPage, setUncontrolledPage] = useState(0);
  const resolvedPagination = resolvePagination(
    pagination,
    allData.length,
    isPageControlled ? controlledPage : uncontrolledPage
  );

  const handlePageChange = useCallback(
    (next: number) => {
      // Clamped here as well as in `resolvePagination`, so a host storing the page
      // in a URL never receives a value it would have to sanitize itself.
      const clamped = Math.min(Math.max(next, 0), resolvedPagination.pageCount - 1);
      if (!isPageControlled) setUncontrolledPage(clamped);
      paginationConfig?.onPageChange?.(clamped);
    },
    [isPageControlled, paginationConfig, resolvedPagination.pageCount]
  );

  /*
   * `totalRows` means the caller is paging on a server and handed us exactly one
   * page, so slicing it again would show a page of a page. Otherwise the table
   * holds the whole set and the slice is ours to take.
   */
  const data = useMemo(() => {
    if (!paginationEnabled || paginationConfig?.totalRows !== undefined) return allData;
    return allData.slice(resolvedPagination.start, resolvedPagination.end);
  }, [allData, paginationEnabled, paginationConfig?.totalRows, resolvedPagination.start, resolvedPagination.end]);

  /*
   * An uncontrolled page that has fallen off the end of a shrinking dataset gets
   * pulled back. `resolvePagination` already clamps what it RENDERS, so this is
   * about the stored value agreeing with it — otherwise paging back from a
   * clamped page 4-of-2 would step to 3 and appear to do nothing twice.
   */
  useEffect(() => {
    if (isPageControlled || !paginationEnabled) return;
    if (uncontrolledPage !== resolvedPagination.page) setUncontrolledPage(resolvedPagination.page);
  }, [isPageControlled, paginationEnabled, uncontrolledPage, resolvedPagination.page]);

  // ── Row selection ─────────────────────────────────────────────────────────
  //
  // Controlled and stateless by design: the table reads a set of ids and reports
  // the next one. Gated on the HANDLER, not on `selectionMode` — a checkbox that
  // reports to nobody is the bug this model exists to remove, and a host that
  // forgot the handler should see no checkboxes rather than dead ones.
  const selectionEnabled = onSelectionChange !== undefined;
  const selectedIds = selectedRowIds ?? NO_SELECTION;

  /**
   * The id selection is keyed on.
   *
   * `getRowId` wins; otherwise a row's own `id`, which is what survives a re-sort
   * and a page change. The positional fallback is last because it does not: it
   * exists so a table of id-less rows still selects rather than throwing.
   */
  const selectionIdFor = useCallback(
    (row: T, index: number): string => {
      if (getRowId) return getRowId(row);
      const id = (row as { id?: unknown }).id;
      if (typeof id === "string" && id.length > 0) return id;
      if (typeof id === "number" && Number.isFinite(id)) return String(id);
      return `row-${index}`;
    },
    [getRowId]
  );

  /**
   * The ids of the rows this table was handed — its "page".
   *
   * Deliberately `data`, not the virtualized window: with virtualization the
   * visible window changes as the user scrolls, and a header checkbox whose
   * meaning moved under the scrollbar would be worse than useless.
   */
  const pageRowIds = useMemo(() => data.map((row, index) => selectionIdFor(row, index)), [data, selectionIdFor]);

  const toggleRow = useCallback(
    (rowId: string, checked: boolean) => {
      if (!onSelectionChange) return;
      // `single` replaces rather than accumulates, which is the whole difference
      // between the two modes; there is no header checkbox for it either.
      if (selectionMode === "single") {
        onSelectionChange(checked ? new Set([rowId]) : new Set());
        return;
      }
      const next = new Set(selectedIds);
      if (checked) next.add(rowId);
      else next.delete(rowId);
      onSelectionChange(next);
    },
    [onSelectionChange, selectedIds, selectionMode]
  );

  const selectedOnPage = pageRowIds.filter((rowId) => selectedIds.has(rowId)).length;
  const allPageSelected = pageRowIds.length > 0 && selectedOnPage === pageRowIds.length;
  const somePageSelected = selectedOnPage > 0 && !allPageSelected;

  const togglePage = useCallback(
    (checked: boolean) => {
      if (!onSelectionChange) return;
      // Starts from the CURRENT selection, so ids the table cannot see survive a
      // header click. Pruning them would silently narrow a bulk action the user
      // built across pages — see `selectedRowIds` in DataTable.types.ts.
      const next = new Set(selectedIds);
      for (const rowId of pageRowIds) {
        if (checked) next.add(rowId);
        else next.delete(rowId);
      }
      onSelectionChange(next);
    },
    [onSelectionChange, pageRowIds, selectedIds]
  );

  /**
   * The checkbox column, synthesized rather than declared.
   *
   * `render` rather than `cellProps`, because only `render` is handed the cell
   * context — and therefore the row index the positional id fallback needs.
   * `mobile: false` keeps it out of the mobile card renderer, where a bare
   * checkbox has no anatomy to sit in (mobile selection is its own design
   * question, not this model's).
   */
  const allColumns = useMemo(() => {
    if (!selectionEnabled) return columns;
    const selectColumn: Column<T> = {
      id: SELECT_COLUMN_ID,
      label: "",
      width: 44,
      align: "center",
      sortable: false,
      reorderable: false,
      exportable: false,
      mobile: false,
      render: (_value, row, context) => {
        const rowId = selectionIdFor(row, context.rowIndex);
        return (
          <SelectCell
            value={undefined}
            row={row}
            context={context}
            isSelected={selectedIds.has(rowId)}
            onSelect={(_row, checked) => toggleRow(rowId, checked)}
          />
        );
      },
    };
    return [selectColumn, ...columns];
  }, [columns, selectionEnabled, selectedIds, selectionIdFor, toggleRow]);

  /**
   * The header checkbox: page-scoped, and `indeterminate` when the page is partly
   * selected. `multi` only — in `single` mode there is no such thing as selecting
   * a page.
   */
  const selectHeaderContent =
    selectionEnabled && selectionMode === "multi" ? (
      <Checkbox
        isSelected={allPageSelected}
        isIndeterminate={somePageSelected}
        onChange={(checked) => togglePage(checked)}
        isDisabled={pageRowIds.length === 0}
        aria-label={allPageSelected ? "Deselect all rows on this page" : "Select all rows on this page"}
      />
    ) : undefined;

  const renderHeaderContent = useCallback(
    (column: Column<T>): ReactNode => (column.id === SELECT_COLUMN_ID ? selectHeaderContent : undefined),
    [selectHeaderContent]
  );

  const [internalSortConfig, setInternalSortConfig] = useState<SortConfig | null>(null);
  // Use controlled sortConfig if provided, otherwise use internal state
  const isControlled = controlledSortConfig !== undefined;
  const localSortConfig = isControlled ? controlledSortConfig : internalSortConfig;
  const tableRef = useRef<HTMLDivElement>(null);

  // Column order state - controlled or uncontrolled. `allColumns`, not `columns`:
  // the checkbox column occupies a grid track like any other, so every place that
  // decides order or width has to see it.
  const defaultColumnOrder = useMemo(() => allColumns.map((col) => col.id), [allColumns]);
  const [internalColumnOrder, setInternalColumnOrder] = useState<string[]>(defaultColumnOrder);

  // Determine if column order is controlled
  const isColumnOrderControlled = controlledColumnOrder !== undefined;
  const localColumnOrder = isColumnOrderControlled ? controlledColumnOrder : internalColumnOrder;

  // Reconcile internalColumnOrder when columns prop changes (uncontrolled mode only)
  useEffect(() => {
    if (isColumnOrderControlled) return;
    setInternalColumnOrder((prev) => {
      const nextIds = allColumns.map((col) => col.id);
      const nextSet = new Set(nextIds);
      const prevSet = new Set(prev);
      // Keep existing columns that still exist, preserving their order
      const filtered = prev.filter((id) => nextSet.has(id));
      // Add any new columns at the end
      const missing = nextIds.filter((id) => !prevSet.has(id));
      return [...filtered, ...missing];
    });
  }, [allColumns, isColumnOrderControlled]);

  // Handle column order change
  const handleColumnOrderChange = useCallback(
    (newOrder: string[]) => {
      if (!isColumnOrderControlled) {
        setInternalColumnOrder(newOrder);
      }
      onColumnOrderChange?.(newOrder);
    },
    [isColumnOrderControlled, onColumnOrderChange]
  );

  // Get ordered columns based on columnOrder
  const orderedColumns = useMemo(() => {
    if (!enableColumnReorder && !controlledColumnOrder) {
      return allColumns;
    }
    const columnMap = new Map(allColumns.map((col) => [col.id, col]));
    // A host-supplied `columnOrder` never mentions the internal checkbox column,
    // so it is put back at the head rather than dropped — the alternative is a
    // selection model that silently disappears when a table is reorderable.
    const ordered = localColumnOrder
      .map((id) => columnMap.get(id))
      .filter((col): col is Column<T> => col !== undefined);
    const selectColumn = columnMap.get(SELECT_COLUMN_ID);
    return selectColumn && !ordered.some((col) => col.id === SELECT_COLUMN_ID) ? [selectColumn, ...ordered] : ordered;
  }, [allColumns, localColumnOrder, enableColumnReorder, controlledColumnOrder]);

  // useInfiniteScroll must be called unconditionally (rules of hooks). A consumer that toggles
  // onLoadMore between renders — e.g. spreading it only when hasMore — would otherwise change the
  // hook count and crash the table ("rendered more hooks than during the previous render"). The hook
  // no-ops its observer when onLoadMore is undefined, so gate the RESULT, not the call: downstream
  // behavior stays identical to the old `onLoadMore ? … : null` when infinite scroll isn't in use.
  const infiniteScrollResult = useInfiniteScroll({
    items: data,
    onLoadMore,
    hasMore,
    isLoading,
    loadingState,
    enableVirtualization,
    estimatedItemSize: estimatedRowHeight || (density === "compact" ? 40 : density === "relaxed" ? 60 : 48),
    overscan: 5,
  });
  // Keep the result when the caller asked for virtualization, even with no `onLoadMore`.
  // Gating the whole result on paging meant an unpaged table — the transformer list, 2,000
  // rows fetched as one ranked scope — silently rendered every row: `virtualizer` was null,
  // so the body fell through to the non-virtualized branch. Measured at 2,000 rows × 8
  // columns: 826ms to mount and 221ms to re-render on identical props, against 15ms/1ms
  // virtualized. That is what made resizing the window crawl.
  //
  // The observer still no-ops without `onLoadMore` (see the hook), so a non-paging table
  // gains virtualization and nothing else.
  const infiniteScroll = onLoadMore || enableVirtualization ? infiniteScrollResult : null;

  // Use infinite scroll refs if available
  const scrollRef = infiniteScroll?.scrollRef ?? tableRef;
  const { loadMoreRef, virtualizer, virtualItems, isLoadingMore, computedLoadingState } = infiniteScroll || {};

  const { gridTemplateColumns, totalMinWidth } = useMemo(() => {
    let minWidthSum = 0;

    const template = orderedColumns
      .map((col) => {
        // Track minWidth for calculating table minimum width.
        // Parses `px` strings as well as numbers: callers commonly pass "200px", and
        // counting those as 0 left `totalMinWidth` at 0 for the whole table, so no
        // minimum was set and the grid squeezed columns instead of scrolling.
        if (col.minWidth) {
          minWidthSum += toPx(col.minWidth);
        } else if (col.width) {
          minWidthSum += toPx(col.width);
        }

        // Explicit width takes precedence
        if (col.width) {
          return typeof col.width === "number" ? `${col.width}px` : col.width;
        }
        // Flex-based sizing
        if (col.flex) {
          return `${col.flex}fr`;
        }
        // Min/max constraints
        if (col.minWidth && col.maxWidth) {
          const min = typeof col.minWidth === "number" ? `${col.minWidth}px` : col.minWidth;
          const max = typeof col.maxWidth === "number" ? `${col.maxWidth}px` : col.maxWidth;
          return `minmax(${min}, ${max})`;
        }
        if (col.minWidth) {
          const min = typeof col.minWidth === "number" ? `${col.minWidth}px` : col.minWidth;
          return `minmax(${min}, 1fr)`;
        }
        if (col.maxWidth) {
          const max = typeof col.maxWidth === "number" ? `${col.maxWidth}px` : col.maxWidth;
          return `minmax(0, ${max})`;
        }
        // Default to equal distribution
        return "1fr";
      })
      .join(" ");

    return { gridTemplateColumns: template, totalMinWidth: minWidthSum };
  }, [orderedColumns]);

  const handleSort = useCallback(
    (columnId: string) => {
      const column = allColumns.find((col) => col.id === columnId);
      if (!column?.sortable) return;

      let newSortConfig: SortConfig;

      if (localSortConfig?.columnId === columnId) {
        // Toggle direction when clicking same column
        newSortConfig = {
          columnId,
          direction: localSortConfig.direction === "asc" ? "desc" : "asc",
        };
      } else {
        // New column, start with asc
        newSortConfig = { columnId, direction: "asc" };
      }

      // Only update internal state if not controlled
      if (!isControlled) {
        setInternalSortConfig(newSortConfig);
      }
      onSort?.(newSortConfig);
    },
    [allColumns, localSortConfig, onSort, isControlled]
  );

  // Determine if we're in initial loading state
  const isInitialLoad = computedLoadingState === "loading" || (isLoading && data.length === 0);

  /*
   * Whether the control is worth drawing at all.
   *
   * Three reasons it would not be, and they compose into one boolean rather than
   * three gates at the render site so the `@container` wrapper below is skipped too
   * — an empty wrapper around a table is a layout box with no purpose.
   *
   *   - paging is off;
   *   - the whole dataset fits on one page, so both arrows are dead and the range
   *     restates a row count the reader can already see (`alwaysShow` opts out — see
   *     `DataTablePagination.alwaysShow` for when a host should);
   *   - the rows are in flight, so any range would be last render's number.
   *
   * Declared HERE rather than beside the render, because `containerStyle` below has
   * to know: a footer and a host height together change which box carries the bound.
   */
  const showPagination =
    paginationEnabled &&
    (paginationConfig?.alwaysShow || resolvedPagination.pageCount > 1) &&
    !isLoading &&
    !isInitialLoad;

  /*
   * A host height plus a footer means the WRAPPER takes the height and the scroll
   * box becomes a flex child of it.
   *
   * `height="100%"` on the scroll box resolves against its parent. Without pagination
   * that parent is the host's own box and the percentage is right. With pagination the
   * wrapper sits between them, and a wrapper with no height of its own is auto-height —
   * so the percentage resolved against content, the rows grew past the pane, and the
   * footer landed below it: clipped where the host sets `overflow: hidden`, spilling
   * into whatever follows where it does not.
   *
   * `flex: 1 1 auto` + `minHeight: 0` gives the table what the footer leaves. The
   * `minHeight` is load-bearing on its own: a flex child refuses to shrink below its
   * content by default, which would hand the full height back and undo the split.
   */
  const paginationOwnsHeight = showPagination && height !== undefined;

  // Container styles for height control
  const containerStyle: React.CSSProperties = {};
  if (height) {
    if (paginationOwnsHeight) {
      containerStyle.flex = "1 1 auto";
      containerStyle.minHeight = 0;
    } else {
      containerStyle.height = typeof height === "number" ? `${height}px` : height;
    }
    containerStyle.overflowY = "auto";
  } else if (maxHeight) {
    containerStyle.maxHeight = typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
    containerStyle.overflowY = "auto";
  } else if (onLoadMore) {
    // Infinite scroll requires explicit height for scrolling
    containerStyle.height = "600px";
    containerStyle.overflowY = "auto";
  }
  if (onLoadMore) {
    // With `onLoadMore` the wrapper class list deliberately omits the overflow
    // utilities so this block owns scrolling — but it only ever set `overflowY`, so a
    // table wider than its pane had nowhere to go and the columns compressed until
    // headers truncated. Horizontal scrolling is what `totalMinWidth` exists to enable.
    containerStyle.overflowX = "auto";
  }

  // Determine wrapper classes based on layout
  // When infinite scroll is enabled, we manage overflow via inline styles
  // Add scrollbar-visible to ensure scrollbars are always visible for accessibility
  const wrapperClasses = twMerge(
    widthClasses[width],
    !onLoadMore && (layout === "responsive" ? "overflow-x-auto scrollbar-visible" : "overflow-auto scrollbar-visible"),
    className
  );

  // Get breakpoint classes for mobile/desktop visibility
  const breakpointClasses = getMobileBreakpointClasses(mobileBreakpoint);
  const showMobile = mobileRenderer !== "none";

  // Determine which mobile renderer to use
  const renderMobileRow = (row: T, index: number) => {
    if (!showMobile) return null;

    if (mobileRenderer === "custom" && customMobileRowRender) {
      return customMobileRowRender(row, index);
    }

    const MobileComponent = mobileRenderer === "cards" ? CardMobileRenderer : AutoMobileRenderer;

    return (
      <MobileComponent
        row={row}
        columns={orderedColumns}
        density={density}
        isLoading={isLoading}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
      />
    );
  };

  // Determine which rows to render (virtualized or all)
  const rowsToRender = virtualizer
    ? virtualItems?.map((virtualItem) => ({
        virtualItem,
        row: data[virtualItem.index],
        index: virtualItem.index,
      })) || []
    : data.map((row, index) => ({ row, index, virtualItem: null }));

  const table = (
    <div
      ref={scrollRef}
      className={wrapperClasses}
      style={{
        ...containerStyle,
        position: virtualizer ? "relative" : undefined,
      }}
    >
      {/* Desktop View */}
      <div
        className={twMerge(
          "grid border-separate border-spacing-0",
          layoutClasses[layout],
          width === "auto" ? "inline-grid w-auto" : "w-full",
          showMobile && breakpointClasses.desktop
        )}
        role="table"
        aria-label={ariaLabel}
        style={{
          gridTemplateColumns,
          // Set minimum width based on sum of column minWidths to enable horizontal scrolling
          minWidth: totalMinWidth > 0 ? `${totalMinWidth}px` : undefined,
        }}
      >
        {/* Header */}
        {!hideHeader &&
          (enableColumnReorder ? (
            <SortableHeaderRow
              columns={allColumns}
              columnOrder={localColumnOrder}
              onColumnOrderChange={handleColumnOrderChange}
              sortConfig={localSortConfig}
              onSort={handleSort}
              stickyHeader={stickyHeader}
              stickyFirstColumn={stickyFirstColumn}
              density={density}
              isLoading={isInitialLoad}
              renderHeaderContent={renderHeaderContent}
              sortNote={sortNote}
            />
          ) : (
            <div className="contents" role="rowgroup">
              {orderedColumns.map((column, index) => (
                <TableHeaderCell
                  key={column.id}
                  column={column}
                  sortConfig={localSortConfig}
                  onSort={handleSort}
                  stickyHeader={stickyHeader}
                  stickyFirstColumn={stickyFirstColumn}
                  isFirstColumn={index === 0}
                  density={density}
                  isLoading={isInitialLoad}
                  headerContent={renderHeaderContent(column)}
                  sortNote={sortNote}
                />
              ))}
            </div>
          ))}

        {/* Body */}
        {virtualizer && !isInitialLoad ? (
          // Virtualized body - wrapping div with virtual height.
          //
          // Only once there is data to virtualize. The virtualizer is built with
          // `count: items.length`, so during an initial load (no rows yet)
          // `getTotalSize()` is 0 — and because the skeletons are laid out by the
          // absolute-positioning branch inside a `position: relative` box, a 0px
          // height collapses every one of them into an invisible sliver. Falling
          // through to the non-virtualized body below keeps the skeleton rows in
          // normal grid flow, which is what makes them visible. Only reachable
          // when `onLoadMore` is set (that's what creates the virtualizer at all),
          // which is why this surfaced with infinite scroll and not before.
          <div
            className="col-span-full"
            style={{
              gridColumn: `1 / -1`,
              height: `${virtualizer.getTotalSize()}px`,
              position: "relative",
            }}
            role="rowgroup"
          >
            {data.length > 0 ? (
              // Virtualized data rows
              <>
                {rowsToRender.map(({ row, index, virtualItem }) => {
                  if (!row || !virtualItem) return null;
                  const rowId = getRowId ? getRowId(row) : `row-${index}`;

                  return (
                    <div
                      key={rowId}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        display: "grid",
                        gridTemplateColumns,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                      data-index={virtualItem.index}
                      ref={virtualizer.measureElement}
                    >
                      <TableRow
                        columns={orderedColumns}
                        row={row}
                        rowIndex={index}
                        density={density}
                        onClick={onRowClick}
                        stickyFirstColumn={stickyFirstColumn}
                        isSelected={selectionEnabled ? selectedIds.has(selectionIdFor(row, index)) : undefined}
                      />
                    </div>
                  );
                })}

                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div
                    style={{
                      position: "absolute",
                      top: `${virtualizer.getTotalSize()}px`,
                      left: 0,
                      width: "100%",
                    }}
                  >
                    {loadingIndicator || <InfiniteScrollIndicator state="loading-more" />}
                  </div>
                )}
              </>
            ) : (
              // Empty state
              <div className="py-12 text-center text-text-secondary">No data available</div>
            )}
          </div>
        ) : (
          // Non-virtualized body
          <div className="contents" role="rowgroup">
            {isInitialLoad ? (
              // Initial loading state - show skeletons
              Array.from({ length: loadingRowCount }).map((_, index) => (
                <LoadingRow
                  key={`loading-${index}`}
                  columns={orderedColumns}
                  rowIndex={index}
                  density={density}
                  stickyFirstColumn={stickyFirstColumn}
                />
              ))
            ) : data.length > 0 ? (
              // Data rows
              <>
                {rowsToRender.map(({ row, index }) => {
                  if (!row) return null;
                  const rowId = getRowId ? getRowId(row) : `row-${index}`;

                  return (
                    <TableRow
                      key={rowId}
                      columns={orderedColumns}
                      row={row}
                      rowIndex={index}
                      density={density}
                      onClick={onRowClick}
                      stickyFirstColumn={stickyFirstColumn}
                      isSelected={selectionEnabled ? selectedIds.has(selectionIdFor(row, index)) : undefined}
                    />
                  );
                })}

                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div className="col-span-full" style={{ gridColumn: `1 / -1` }}>
                    {loadingIndicator || <InfiniteScrollIndicator state="loading-more" />}
                  </div>
                )}
              </>
            ) : (
              // Empty state
              <div className="col-span-full py-12 text-center text-text-secondary" role="row">
                <div role="cell">No data available</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile View */}
      {showMobile && (
        <div className={breakpointClasses.mobile}>
          {isInitialLoad ? (
            // Mobile initial loading state
            Array.from({ length: loadingRowCount }).map((_, index) => (
              <MobileLoadingSkeleton key={`mobile-loading-${index}`} density={density} />
            ))
          ) : data.length > 0 ? (
            // Mobile data rows
            <>
              {rowsToRender.map(({ row, index }) => {
                if (!row) return null;
                const rowId = getRowId ? getRowId(row) : `mobile-row-${index}`;
                return (
                  <div key={rowId} className="border-b border-border-default last:border-b-0">
                    {renderMobileRow(row, index)}
                  </div>
                );
              })}

              {/* Mobile loading more indicator */}
              {isLoadingMore && (loadingIndicator || <InfiniteScrollIndicator state="loading-more" />)}
            </>
          ) : (
            // Mobile empty state
            <div className="py-12 text-center text-text-secondary">No data available</div>
          )}
        </div>
      )}

      {/* Intersection observer target - positioned based on virtualization */}
      {onLoadMore &&
        hasMore &&
        !isInitialLoad &&
        (virtualizer ? (
          // For virtualized: position at the end of virtual content
          <div
            ref={loadMoreRef}
            style={{
              position: "absolute",
              top: `${virtualizer.getTotalSize()}px`,
              left: 0,
              width: "100%",
              height: 1,
            }}
          />
        ) : (
          // For non-virtualized: simple element at the end
          <div ref={loadMoreRef} className="h-px w-full" />
        ))}
    </div>
  );

  if (!showPagination) return table;

  return (
    /*
     * `@container` sits on this WRAPPER, not on the footer: an element cannot match
     * a container query it declares itself. This is what lets the footer respond to
     * the TABLE's width rather than the window's — a 380px side panel on a wide
     * monitor gets the mobile control row, which a viewport breakpoint would miss.
     */
    <div
      className="@container flex flex-col"
      style={
        paginationOwnsHeight ? { height: typeof height === "number" ? `${height}px` : height, minHeight: 0 } : undefined
      }
    >
      {table}
      {/*
        Outside the scroll box, so the control stays put while the rows move under
        it. Inside, it would scroll away with the content it pages — unusable on
        exactly the bounded tables most likely to want it.

        Whether it renders at all is `showPagination` above.
      */}
      <PaginationFooter
        {...resolvedPagination}
        density={density}
        onPageChange={handlePageChange}
        {...(ariaLabel ? { label: `${ariaLabel} pagination` } : {})}
      />
    </div>
  );
}
